import { isCustomElementRegistered } from './register';
import { getAssetsUrl } from '../hn-page';
import { error } from '../logger';
const isTemplateRenderedServerSide = (templateElement) => {
    if (templateElement && templateElement.content.children.length > 0) {
        return true;
    }
    return false;
};
const createMicrowebTemplate = (templateName) => {
    const templateElement = document.createElement('template');
    templateElement.setAttribute('id', templateName);
    document.head.appendChild(templateElement);
    return templateElement;
};
/**
 * Finn det første link-elementet i document.head som inneholder helsenorge.css
 * @returns URL til helsenorge.css i document.head, med hash i URLen
 */
const getHelsenorgeCssUrl = () => {
    const elementList = document.head.querySelectorAll(`link[href^="${getAssetsUrl()}/core/static/css/helsenorge."]`);
    const linkElement = Array.from(elementList)[0];
    return linkElement?.href;
};
const blockUntilEvent = (target, event) => {
    return new Promise(resolve => target.addEventListener(event, resolve, { passive: true, once: true }));
};
/**
 * Denne kan brukes for å fetche en web component som finnes.
 * Legger til <link> og <script> tags for relevant webcomponent så riktig js og css lastes, og oppretter element i dom'en.
 *
 * Tilsvarer "metode 2" for konsumering av web component som er dokumentert på confluence-siden for microfrontend:
 * https://confluence.helsedirektoratet.no/display/HR2/@helsenorge+Microfrontend
 *
 * Eksempel:
 * // Lag template én gang, i html'en eller på root. Fetch nødvendige assets med fetchWebComp
 * <template id="hn-webcomp-oppgave-template"></template>
 * // fetch assets, vanligvis i en UseEffect
 * fetchWebComp({domain={`${getAssetsUrl()}/oppgave`} entryName="webcomp-oppgave" componentName="hn-webcomp-oppgave" includeHelsenorgeCss});
 * // Lag flere instans av følgende i din react kode:
 * <hn-webcomp-oppgave></hn-webcomp-oppgave>
 */
export const fetchWebComp = async (config) => {
    // Venter med å sjekke om template er allerede lagt til server-side inntil HTML-en er innlastet.
    // Dette for å unngå undøvendig fetch av assets.json pga. race condition.
    if (document.readyState === 'loading') {
        await blockUntilEvent(document, 'DOMContentLoaded');
    }
    const { domain, componentName, entryName, includeHelsenorgeCss, templateName } = config;
    const microwebTemplate = document.getElementById(templateName || `${componentName}-template`);
    if (!isCustomElementRegistered(componentName) && !isTemplateRenderedServerSide(microwebTemplate)) {
        const assetsUrl = `${domain}/assets.json`;
        // Sørg for å alltid hente nyeste assets.json
        // https://developer.mozilla.org/en-US/docs/Web/API/Request/cache
        return fetch(assetsUrl, { cache: 'no-cache' })
            .then(response => {
            if (response.status === 200) {
                const resp = response.json();
                return resp;
            }
            return Promise.reject(Error(`Error while fetching asset-manifest.json from ${domain}/assets.json`));
        })
            .then((assets) => {
            const templateElement = microwebTemplate ?? createMicrowebTemplate(templateName || `${componentName}-template`);
            const appendStylesheet = (href) => {
                const cssElement = document.createElement('link');
                cssElement.setAttribute('href', href);
                cssElement.rel = 'stylesheet';
                if (templateElement)
                    templateElement.content.appendChild(cssElement);
            };
            const appendScript = (src, type) => {
                const scriptElement = document.createElement('script');
                scriptElement.setAttribute('src', src);
                if (type) {
                    scriptElement.setAttribute('type', type);
                }
                document.head.appendChild(scriptElement);
            };
            if (includeHelsenorgeCss) {
                const helsenorgeCssUrl = getHelsenorgeCssUrl();
                appendStylesheet(helsenorgeCssUrl || `${getAssetsUrl()}/core/static/css/helsenorge/helsenorge.css`);
            }
            assets[entryName]?.css?.forEach(asset => {
                appendStylesheet(`${domain}/${asset}`);
            });
            if (assets[entryName]) {
                appendScript(`${domain}/${assets[entryName]?.file}`, 'module');
            }
        })
            .catch(e => {
            error(e, assetsUrl);
        });
    }
    return Promise.resolve();
};
